import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { BaseDialogProps } from "./Dialog.types";
import "./Dialog.scss";
import { Icon } from "../../Icon/Icon";
import { Button } from "../Button";

export const Dialog: React.FC<BaseDialogProps> = ({
  children,
  variant = "primary",
  size = "md",
  visible = false,
  title,
  content,
  closeOnAction = true,
  onClose,
  onConfirm,
  onCancel,
  confirmText = "confirm",
  cancelText = "cancel",
  className = "",
  testId,
  showCloseButton = false,
  closeAnimation = false,
  customActions,
  closeButtonPosition = "top-right",
  getContainer,
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isManualClosingRef = useRef(false);
  const maskRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);

  onCloseRef.current = onClose;

  useEffect(() => {
    if (visible) {
      // 打开弹窗 - 但如果正在手动关闭，不要重置状态
      if (!isManualClosingRef.current) {
        setShouldShow(true);
        setIsClosing(false);
      }
    } else if (shouldShow && !isClosing) {
      // visible 变为 false 时，根据 closeAnimation 决定是否执行动画
      if (closeAnimation) {
        setIsClosing(true);
        const timer = setTimeout(() => {
          setShouldShow(false);
          setIsClosing(false);
          isManualClosingRef.current = false;
        }, 550);

        return () => clearTimeout(timer);
      } else {
        // 不执行动画，直接关闭
        setShouldShow(false);
        isManualClosingRef.current = false;
      }
    }
  }, [visible, shouldShow, isClosing, closeAnimation]);

  // 原生 DOM 事件兜底：当 React 合成事件因 KeepAlive 冻结而失效时，
  // 通过原生事件监听确保关闭按钮和遮罩点击始终可用
  useEffect(() => {
    const mask = maskRef.current;
    if (!mask) return;

    const handleNativeClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // 点击了关闭按钮
      const isCloseBtn = target.closest(".dialog__close-button");
      // 点击了遮罩背景（target 就是 mask 本身）
      const isMaskBg = target === mask && closeOnAction;

      if (isCloseBtn || isMaskBg) {
        // 直接隐藏 DOM
        mask.style.display = "none";
        try {
          onCloseRef.current?.();
        } catch (_e) {
          // 忽略：组件可能已被冻结
        }
      }
    };

    mask.addEventListener("click", handleNativeClick, true);
    return () => {
      mask.removeEventListener("click", handleNativeClick, true);
    };
  });

  if (!shouldShow) return null;

  const dialogClass = [
    "dialog",
    `dialog--${variant}`,
    `dialog--${size}`,
    showCloseButton ? "dialog--with-close-button" : "",
    `dialog--close-${closeButtonPosition}`,
    isClosing ? "dialog--closing" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // 构建 actions 数组
  const actions = [];
  if (onCancel) {
    actions.push({
      key: "cancel",
      text: cancelText,
      onClick: onCancel,
    });
  }
  if (onConfirm) {
    actions.push({
      key: "confirm",
      text: confirmText,
      onClick: onConfirm,
    });
  }

  // 处理关闭
  const handleClose = () => {
    if (isClosing) return;

    isManualClosingRef.current = true;

    if (closeAnimation) {
      setIsClosing(true);

      setTimeout(() => {
        setShouldShow(false);
        setIsClosing(false);
        isManualClosingRef.current = false;
        onClose?.();
      }, 550);
    } else {
      setShouldShow(false);
      isManualClosingRef.current = false;
      onClose?.();
    }
  };

  // 处理遮罩点击
  const handleMaskClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closeOnAction) {
      handleClose();
    }
  };

  // 获取挂载容器
  const getContainerElement = (): HTMLElement => {
    if (getContainer === null) {
      return document.body;
    }
    if (getContainer instanceof HTMLElement) {
      return getContainer;
    }
    if (typeof getContainer === "function") {
      return getContainer();
    }
    // 默认挂载到root元素
    return document.getElementById("root") || document.body;
  };

  const dialogContent = (
    <div
      ref={maskRef}
      className={`dialog__mask ${isClosing ? "dialog__mask--closing" : ""}`}
      onClick={handleMaskClick}
    >
      {/* 关闭按钮 - 顶部位置 */}
      {showCloseButton &&
        (closeButtonPosition === "top-left" ||
          closeButtonPosition === "top-right") && (
          <button
            className="dialog__close-button"
            onClick={handleClose}
            aria-label="close"
          >
            <Icon name="closeDialog" color="#000" />
          </button>
        )}

      <div className={dialogClass} data-testid={testId}>
        {/* 弹窗内容 */}
        <div className="dialog__content">
          {/* 标题区域 */}
          {title && <div className="dialog__title">{title}</div>}

          {/* 主要内容区域 */}
          <div className="dialog__body">{content || children}</div>

          {/* 默认按钮区域 */}
          {!customActions && actions.length > 0 && (
            <div className="dialog__actions">
              {actions.map(action => (
                <Button
                  key={action.key}
                  className={`dialog__button dialog__button--${action.key}`}
                  variant={action.key === "confirm" ? "primary" : "cancel"}
                  block
                  onClick={action.onClick}
                >
                  {action.text}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 自定义按钮区域 - 在弹窗外部 */}
      {customActions && (
        <div className="dialog__custom-actions">{customActions}</div>
      )}

      {/* 关闭按钮 - 底部位置 */}
      {showCloseButton &&
        (closeButtonPosition === "bottom-left" ||
          closeButtonPosition === "bottom-right" ||
          closeButtonPosition === "bottom-center") && (
          <div className="dialog__close-button" onClick={handleClose} role="button" aria-label="close">
            <Icon name="closeDialog" color="#ffffff" />
          </div>
        )}
    </div>
  );

  return createPortal(dialogContent, getContainerElement());
};

export default Dialog;
